export type CardTheme = 'light' | 'dark';
export type CardFormat = 'portrait' | 'landscape';

export type VerseCardOpts = {
  verseText: string;
  reference: string;
  theme: CardTheme;
  format: CardFormat;
};

export type MilestoneCardOpts = {
  icon: string;
  label: string;
  subtitle: string;
  theme: CardTheme;
  format: CardFormat;
};

type Palette = {
  bg: string;
  ink: string;
  muted: string;
  accent: string;
};

const PALETTES: Record<CardTheme, Palette> = {
  light: { bg: '#f3eddc', ink: '#2c2416', muted: '#8a7355', accent: '#c8b87a' },
  dark:  { bg: '#0e1421', ink: '#e8dfc4', muted: '#8898b0', accent: '#3a4a60' },
};

const DIMS: Record<CardFormat, { w: number; h: number }> = {
  portrait:  { w: 1080, h: 1350 },
  landscape: { w: 1200, h: 630 },
};

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawInnerBorder(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  inset: number,
  accent: string,
): void {
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.strokeRect(inset, inset, w - inset * 2, h - inset * 2);
}

function drawWatermark(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  muted: string,
): void {
  const size = Math.round(w * 0.018);
  ctx.font = `${size}px "system-ui, -apple-system, sans-serif"`;
  ctx.fillStyle = muted;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.globalAlpha = 0.6;
  ctx.fillText('Scripture · FCBH Reader', w / 2, h - 36);
  ctx.globalAlpha = 1;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });
}

export async function renderVerseCard(opts: VerseCardOpts): Promise<Blob> {
  const { w, h } = DIMS[opts.format];
  const pal = PALETTES[opts.theme];
  const inset = 40;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, w, h);

  drawInnerBorder(ctx, w, h, inset, pal.accent);

  const textArea = w - inset * 2 - 80;
  const verseFontSize = Math.round(w * 0.035);
  ctx.font = `italic ${verseFontSize}px "Georgia, 'Times New Roman', serif"`;
  ctx.fillStyle = pal.ink;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  const lines = wrapText(ctx, `“${opts.verseText}”`, textArea);
  const lineHeight = verseFontSize * 1.55;
  const blockHeight = lines.length * lineHeight;
  const refFontSize = Math.round(verseFontSize * 0.62);
  const refGap = Math.round(verseFontSize * 0.9);
  const totalHeight = blockHeight + refGap + refFontSize;
  let y = (h - totalHeight) / 2;

  for (const line of lines) {
    ctx.fillText(line, w / 2, y);
    y += lineHeight;
  }

  y += refGap;
  ctx.font = `${refFontSize}px "Georgia, 'Times New Roman', serif"`;
  ctx.fillStyle = pal.muted;
  ctx.fillText(`— ${opts.reference}`, w / 2, y);

  drawWatermark(ctx, w, h, pal.muted);

  return canvasToBlob(canvas);
}

export async function renderMilestoneCard(opts: MilestoneCardOpts): Promise<Blob> {
  const { w, h } = DIMS[opts.format];
  const pal = PALETTES[opts.theme];
  const inset = 40;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, w, h);

  drawInnerBorder(ctx, w, h, inset, pal.accent);

  const iconCenterY = h * 0.47;
  const iconSize = Math.round(h * 0.20);

  const subtitleSize = Math.round(w * 0.022);
  ctx.font = `${subtitleSize}px "system-ui, -apple-system, sans-serif"`;
  ctx.fillStyle = pal.muted;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const subtitleY = iconCenterY - iconSize * 0.65;
  ctx.fillText(opts.subtitle.toUpperCase(), w / 2, subtitleY);

  ctx.font = `${iconSize}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(opts.icon, w / 2, iconCenterY);

  const labelSize = Math.round(w * 0.048);
  ctx.font = `600 ${labelSize}px "Georgia, 'Times New Roman', serif"`;
  ctx.fillStyle = pal.ink;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(opts.label, w / 2, iconCenterY + iconSize * 0.6);

  drawWatermark(ctx, w, h, pal.muted);

  return canvasToBlob(canvas);
}
